import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ViewMode } from '../types';
import { TrendingUp, Users, Briefcase, CreditCard, DollarSign, BarChart3, LogOut } from 'lucide-react';
import { User } from '../services/auth';

interface SidebarProps {
  user: User | null;
  onLogout: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ user, onLogout }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const menuItems = [
    {
      path: '/overview',
      label: 'Overview',
      icon: TrendingUp,
      description: 'Dashboard & Summary',
    },
    {
      path: '/family',
      label: 'Family',
      icon: Users,
      description: 'Manage family members & investments',
    },
    {
      path: '/projections',
      label: 'Projections',
      icon: BarChart3,
      description: 'Financial projections & forecasts',
    },
    {
      path: '/income-expenses',
      label: 'Cash Flow',
      icon: DollarSign,
      description: 'Manage income sources & expenses',
    },
  ];

  const handleLogout = () => {
    onLogout();
    navigate('/');
  };

  return (
    <div className="hidden md:flex fixed left-0 top-0 h-screen w-64 bg-white shadow-lg border-r border-gray-200 flex-col">
      {/* Logo */}
      <div className="p-6 border-b border-gray-200 flex-shrink-0">
        <div className="flex items-center space-x-3">
          <div>
            <img src="/HGI-icon.png" alt="HomeGrowInvest" className="h-10" />
          </div>
          <div>
              <h1 className="text-xl font-bold text-gray-900">HomeGrow<span className="text-primary-600">Invest</span></h1>
              <p className="text-gray-500">Financial Planner</p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 overflow-y-auto">
        <ul className="space-y-2">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path || 
              (item.path === '/income-expenses' && (location.pathname === '/income' || location.pathname === '/expenses')) ||
              (item.path === '/family' && (location.pathname === '/family-members' || location.pathname === '/investments')) ||
              (item.path === '/projections' && location.pathname === '/financial-model');
            
            return (
              <li key={item.path}>
                <Link
                  to={item.path}
                  className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-colors ${
                    isActive
                      ? 'bg-primary-50 text-primary-700 border border-primary-200'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <Icon className={`w-5 h-5 ${isActive ? 'text-primary-600' : 'text-gray-400'}`} />
                  <div className="flex-1">
                    <div className={`font-medium ${isActive ? 'text-primary-700' : 'text-gray-900'}`}>
                      {item.label}
                    </div>
                    <div className="text-xs text-gray-500">{item.description}</div>
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* User Info & Logout */}
      <div className="p-4 border-t border-gray-200 flex-shrink-0">
        {user && (
          <div className="mb-3">
            <div className="text-xs text-gray-500 mb-1">Logged in as</div>
            <div className="text-sm font-semibold text-gray-900 truncate">{user.name}</div>
            <div className="text-xs text-gray-500 truncate">{user.email}</div>
          </div>
        )}
        <button
          onClick={handleLogout}
          className="w-full flex items-center justify-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
        >
          <LogOut className="w-4 h-4" />
          <span>Logout</span>
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
